// src/pages/staff/StaffListPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  People as PeopleIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Hooks
import { useStaff } from '../hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';

// Utils
import { UUIDDisplay } from '../../../utils/formatters/uuid';
import { capitalize } from '../../../utils/formatters/capitalize';

// Components
import ProfilePicture from '../../../components/common/ProfilePicture';

import PageHeader from '../../../components/common/PageHeader';

export default function StaffListPage() {
  const { t } = useTranslation(['staff', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();

  // State
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  // API hooks
  const {
    staff,
    loading,
    error,
    clearError,
    fetchDepartmentStaff,
    deleteStaff,
  } = useStaff();

  // Load initial data
  useEffect(() => {
    fetchDepartmentStaff({ include_inactive: includeInactive });
  }, [fetchDepartmentStaff, includeInactive]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchDepartmentStaff({ include_inactive: includeInactive });
  }, [fetchDepartmentStaff, includeInactive]);

  const handleIncludeInactiveChange = useCallback((event) => {
    setIncludeInactive(event.target.checked);
  }, []);

  const handleCreateClick = useCallback(() => {
    navigate('/staff/create');
  }, [navigate]);

  const handleViewStaff = useCallback((staffMember) => {
    navigate(`/staff/${staffMember.staff_member_id}`);
  }, [navigate]);

  const handleEditStaff = useCallback((staffMember) => {
    navigate(`/staff/${staffMember.staff_member_id}/edit`);
  }, [navigate]);

  const handleMenuOpen = useCallback((event, staffMember) => {
    event.stopPropagation();
    setSelectedStaff(staffMember);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedStaff(null);
  }, []);

  const handleDeleteStaff = useCallback((staffMember) => {
    showConfirmDialog({
      title: t('staff:confirm.delete_staff_title'),
      message: t('staff:confirm.delete_staff_message', { name: staffMember.name }),
      confirmText: t('staff:confirm.delete_confirm'),
      cancelText: t('common:buttons.cancel'),
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteStaff(staffMember.staff_member_id);
          if (result.success) {
            showSuccess(t('staff:success.staff_deleted'));
          } else {
            showError(result.error || t('staff:errors.delete_failed'));
          }
        } catch (error) {
          showError(t('staff:errors.delete_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteStaff, showSuccess, showError, handleMenuClose, t]);

  // Check permissions
  const canCreate = permissions.hasScope('staff:create');
  const canUpdate = permissions.hasScope('staff:update');
  const canDelete = permissions.hasScope('staff:delete');

  // Filtered staff
  const filteredStaff = useMemo(() => {
    return staff.filter(member => includeInactive || member.is_active);
  }, [staff, includeInactive]);

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('staff:grid.columns.staff_member'),
      flex: 1,
      minWidth: 250,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
            <ProfilePicture
              profilePictureFileId={params.row?.profile_picture_file_id}
              name={params.value}
              size={32}
              imageSize="32x32"
              color={params.row?.color}
            />
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" fontWeight="medium">
                  {capitalize(params.value)}
                </Typography>
                {!params.row.is_active && (
                  <Chip
                    label={t('staff:status.inactive')}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t('common:labels.id')} <UUIDDisplay uuid={params.row.staff_member_id} />
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'roles_count',
      headerName: t('staff:grid.columns.roles'),
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: t('staff:grid.columns.status'),
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          icon={params.value ? <CheckCircleIcon /> : <BlockIcon />}
          label={params.value ? t('staff:status.active') : t('staff:status.inactive')}
          size="small"
          color={params.value ? 'success' : 'warning'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'last_login',
      headerName: t('staff:grid.columns.last_login'),
      width: 160,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value 
            ? <LocalizedDateDistance date={params.value} />
            : t('staff:grid.columns.never_logged_in')
          }
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: t('staff:grid.columns.created'),
      width: 140,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? <LocalizedDateDistance date={params.value} /> : t('staff:grid.columns.unknown')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      // Icon-only kebab column: no header, like the other list grids
      headerName: '',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ], [handleMenuOpen, t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <PageHeader
        icon={PeopleIcon}
        title={t('staff:title')}
        subtitle={t('staff:subtitle')}
        actions={<>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={handleIncludeInactiveChange}
                size="small"
              />
            }
            label={t('staff:actions.include_inactive')}
          />

          <Tooltip title={t('staff:actions.refresh')}>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              disabled={loading}
            >
              {t('staff:actions.add_staff_member')}
            </Button>
          )}
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* DataGrid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-main': {
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                '&:focus': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            },
          },
        }}
      >
        <DataGrid
          rows={filteredStaff}
          columns={columns}
          getRowId={(row) => row.staff_member_id}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          onRowClick={(params) => handleViewStaff(params.row)}
          rowHeight={72}
          getRowHeight={() => 'auto'}
          slots={{
            noRowsOverlay: () => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                py={4}
              >
                <PeopleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('staff:messages.no_staff')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  {includeInactive 
                    ? t('staff:messages.no_staff_description')
                    : t('staff:messages.no_active_staff')
                  }
                </Typography>
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateClick}
                  >
                    {t('staff:messages.create_first_staff')}
                  </Button>
                )}
              </Box>
            ),
          }}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
        }}
      >
        <MenuItem onClick={() => { handleViewStaff(selectedStaff); handleMenuClose(); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('staff:actions.view_details')}</ListItemText>
        </MenuItem>

        {canUpdate && (
          <MenuItem onClick={() => { handleEditStaff(selectedStaff); handleMenuClose(); }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('staff:actions.edit')}</ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem onClick={() => { handleDeleteStaff(selectedStaff); handleMenuClose(); }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography color="error">{t('staff:actions.delete')}</Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </Box>
  );
}